import React, { Fragment, memo, useCallback } from 'react'
import tw from 'twin.macro'
import { Form, Formik } from 'formik'
import { Typography } from '@mui/material'
import { useSnackbar } from 'notistack'
import { useSelector } from 'react-redux'
import { Button } from 'src/components/shared/Button'
import { CommonModal } from 'src/components/shared/CommonModal'
import { FormikTextArea } from 'src/components/shared/form/formik/FormikTextArea'
import { AdminDiscardCapturedEvent } from 'src/constants/actionTypes'
import { capturedEventSourceLabels } from 'src/constants/payments-ops/labels'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import type { CapturedEvent, FetchCapturedEventsPayload } from 'src/interfaces/usBankKillswitch'
import { getCapturedEventReplaying } from 'src/selectors/usBankKillswitch'
import { discardEventValidator } from './DiscardEventModal.validator'

interface Props {
  /** `null` renders nothing, so the form starts empty on every open. */
  event: CapturedEvent | null
  /** The list filter to refetch with once the discard lands. */
  refetch: FetchCapturedEventsPayload
  onClose(): void
}

interface DiscardFormValues {
  notes: string
}

const FORM_ID = 'discard-captured-event-form'
const INITIAL_VALUES: DiscardFormValues = { notes: '' }

const DiscardEventModalComponent = ({ event, refetch, onClose }: Props) => {
  const snackbar = useSnackbar()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()
  const isBusy = useSelector(getCapturedEventReplaying)

  const handleClose = useCallback(() => {
    // The discard is in flight: closing now would hide its outcome.
    if (isBusy) {
      return
    }

    onClose()
  }, [isBusy, onClose])

  const handleSubmit = useCallback(
    async (values: DiscardFormValues) => {
      if (!event) {
        return
      }

      const result = await asyncDispatchWithErrorToast(
        AdminDiscardCapturedEvent.request({
          eventId: event.id,
          notes: values.notes.trim(),
          refetch,
        }),
      )

      if (!result) {
        return
      }

      snackbar?.enqueueSnackbar('Event discarded', { variant: 'success' })
      onClose()
    },
    [asyncDispatchWithErrorToast, event, onClose, refetch, snackbar],
  )

  if (!event) {
    return null
  }

  return (
    <Formik<DiscardFormValues>
      initialValues={INITIAL_VALUES}
      validationSchema={discardEventValidator}
      onSubmit={handleSubmit}
    >
      <CommonModal
        isOpen
        title="Discard captured event?"
        className={tw`max-w-[480px]`}
        onClose={handleClose}
        buttons={
          <Fragment>
            <Button size="small" color="grey" disabled={isBusy} onClick={handleClose}>
              Cancel
            </Button>
            <Button
              size="small"
              color="red"
              type="submit"
              form={FORM_ID}
              loading={isBusy}
              disabled={isBusy}
              data-testid="discard-event-confirm"
            >
              Discard event
            </Button>
          </Fragment>
        }
      >
        <Form id={FORM_ID} css={tw`flex flex-col gap-4`}>
          <Typography variant="body2">
            {capturedEventSourceLabels[event.source] ?? event.source} event {event.id} will never be
            replayed. This cannot be undone.
          </Typography>
          <FormikTextArea<DiscardFormValues>
            name="notes"
            label="Notes *"
            placeholder="Why is this being discarded?"
            minRows={3}
            fullWidth
          />
        </Form>
      </CommonModal>
    </Formik>
  )
}

export const DiscardEventModal = memo(DiscardEventModalComponent)
